import React from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useReads } from "../hooks/useReads";
import { getCoreToken } from "../web3/contracts";
import { fmtUnits, shortAddress } from "../utils/format";
import { CORE_DECIMALS, ACTIVE_CONTRACTS } from "../config/app.config";
import StatGrid from "../components/admin/StatGrid";
import ActionForm from "../components/admin/ActionForm";
import RoleBanner from "../components/admin/RoleBanner";
import AddressBar from "../components/admin/AddressBar";

const addr = (k, label, ph = "0x...") => ({ key: k, label, placeholder: ph });

export default function CoreTokenAdmin() {
  const { signer } = useWeb3();
  const { data, reload } = useReads(async (provider) => {
    const t = getCoreToken(provider);
    const [owner, minter, name, symbol, supply] = await Promise.all([
      t.owner(), t.minter(), t.name(), t.symbol(), t.totalSupply(),
    ]);
    return { owner, minter, name, symbol, supply };
  });

  const C = (s) => getCoreToken(s);

  return (
    <div className="stack">
      <AddressBar label="CORE Token Contract" address={ACTIVE_CONTRACTS.coreToken} />
      {/* CoreToken has an owner only (no separate admin role). */}
      <RoleBanner owner={data?.owner} admin={data?.owner} />

      <StatGrid
        title="CORE Token — Overview" icon="coins" accent="violet" onRefresh={reload}
        rows={data ? [
          { label: "Name", value: `${data.name} (${data.symbol})` },
          { label: "Owner", value: shortAddress(data.owner), mono: true },
          { label: "Minter", value: data.minter === ethers.constants.AddressZero ? "Not set" : shortAddress(data.minter), mono: true },
          { label: "Total Supply", value: `${fmtUnits(data.supply, CORE_DECIMALS, 2)} CORE` },
        ] : []}
      />

      <div className="adm-section-title">Minter</div>
      <div className="adm-grid">
        <ActionForm title="Change Minter" desc="Set the address allowed to mint CORE (e.g. the mining contract, or your wallet to mint manually)." icon="spark" accent="gold"
          fields={[addr("a", "New minter address")]}
          button="Set Minter" onDone={reload}
          onRun={(v) => C(signer).setMinter(v.a)} />

        <ActionForm title="Mint CORE" desc="Mints CORE instantly. Only works if YOUR wallet is the current minter (set it above first)." icon="coins" accent="success"
          fields={[addr("a", "To address"), { key: "amt", label: "Amount (CORE)", placeholder: "1000", numeric: true }]}
          button="Mint CORE" onDone={reload}
          onRun={(v) => C(signer).mint(v.a, ethers.utils.parseUnits(String(v.amt || "0"), CORE_DECIMALS))} />
      </div>

      <div className="adm-section-title">Recovery & ownership</div>
      <div className="adm-grid">
        <ActionForm title="Recover Stuck Tokens" desc="Recover a non-CORE ERC-20 sent to the contract. Amount in whole units (18 decimals)." icon="shield"
          fields={[addr("t", "Token address"), { key: "amt", label: "Amount", placeholder: "100", numeric: true }]}
          button="Recover Tokens" onDone={reload}
          onRun={(v) => C(signer).recoverTokens(v.t, ethers.utils.parseUnits(String(v.amt || "0"), 18))} />

        <ActionForm title="Transfer Ownership" desc="Irreversible — double-check the address." icon="shield" danger
          fields={[addr("a", "New owner address")]}
          button="Transfer Ownership" onDone={reload}
          onRun={(v) => C(signer).transferOwnership(v.a)} />
      </div>
    </div>
  );
}
